import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LayoutTemplate } from 'lucide-react';

/** Normalize Fabric JSON from API (object, JSON string, or double-encoded string). */
function parseTemplateJson(template) {
  if (!template || template.json == null) return null;
  let raw = template.json;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object') return null;
  return raw;
}

function isKonvaTemplateJson(json) {
  return !!(json && (json.editor === 'konva' || (json.scene && Array.isArray(json.scene.objects))));
}

function imageSrcOpts(src) {
  const s = String(src || '');
  if (s.startsWith('data:') || s.startsWith('blob:')) return {};
  if (/^https?:\/\//i.test(s)) return { crossOrigin: 'anonymous' };
  return {};
}

/**
 * Renders a small JPEG from template Fabric JSON when `preview_image` is missing or invalid.
 * Uses IntersectionObserver with the library scroll root so cards inside overflow panels still trigger.
 */
export default function TemplateFabricThumb({ template, listMode = false }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | visible | loading | ready | error
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const runIdRef = useRef(0);

  const json = useMemo(() => parseTemplateJson(template), [template?.json]);
  const rawW = Math.max(1, Math.min(6000, Number(template?.width) || 300));
  const rawH = Math.max(1, Math.min(6000, Number(template?.height) || 400));
  const tplKey = String(template?._id ?? template?.id ?? '');
  const savedPreview = template?.preview_image || template?.previewImage;
  const hasSavedPreview =
    typeof savedPreview === 'string' && savedPreview.trim().startsWith('data:image/');

  useEffect(() => {
    if (!json) {
      setStatus('error');
      return undefined;
    }
    if (isKonvaTemplateJson(json)) {
      setStatus('konva');
      return undefined;
    }
    setDataUrl(null);
    setStatus('idle');

    const el = wrapRef.current;
    if (!el) return undefined;

    const scrollRoot = el.closest('[data-library-scroll]');
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStatus((s) => (s === 'ready' || s === 'error' ? s : 'visible'));
        }
      },
      { root: scrollRoot || null, rootMargin: '160px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [json, tplKey, rawW, rawH]);

  useEffect(() => {
    if (status !== 'visible' || !json) return undefined;

    const myRun = ++runIdRef.current;
    setStatus('loading');

    let cancelled = false;
    let fabricCanvas = null;
    let scheduleId;

    const dispose = () => {
      if (fabricCanvas) {
        try {
          fabricCanvas.dispose();
        } catch {
          /* ignore */
        }
        fabricCanvas = null;
      }
    };

    const start = () => {
      if (cancelled || myRun !== runIdRef.current) return;

      (async () => {
        try {
          const mod = await import('fabric');
          const fabric = mod.fabric || mod.default;
          if (!fabric || !fabric.StaticCanvas || cancelled || myRun !== runIdRef.current) return;

          const customs = fabric.Object.customProperties || [];
          ['fieldKey', 'fieldConfig', '_shapeLabelGroup'].forEach((p) => {
            if (!customs.includes(p)) customs.push(p);
          });
          fabric.Object.customProperties = customs;

          const elCanvas = canvasRef.current;
          if (!elCanvas || cancelled || myRun !== runIdRef.current) return;

          const mw = rawW;
          const mh = rawH;

          fabricCanvas = new fabric.StaticCanvas(elCanvas, {
            width: mw,
            height: mh,
            backgroundColor: '#ffffff',
            renderOnAddRemove: false,
          });

          const maxEdge = listMode ? 640 : 900;
          const z = Math.min(1, maxEdge / Math.max(mw, mh));
          if (z < 1) {
            fabricCanvas.setDimensions({ width: mw * z, height: mh * z });
            fabricCanvas.setViewportTransform([z, 0, 0, z, 0, 0]);
          }

          await new Promise((resolve) => {
            let imageLoadsPending = 0;
            let structureReady = false;
            let settled = false;
            let rasterDone = false;
            let stallSafe;
            let failSafe;

            const finish = () => {
              if (settled || cancelled || myRun !== runIdRef.current) return;
              if (stallSafe) window.clearTimeout(stallSafe);
              if (failSafe) window.clearTimeout(failSafe);
              settled = true;
              resolve();
            };

            const tryRasterize = () => {
              if (cancelled || myRun !== runIdRef.current) return;
              if (!structureReady || imageLoadsPending > 0) return;
              if (rasterDone) return;
              rasterDone = true;
              try {
                fabricCanvas.renderAll();
                const mult = Math.min(0.45, 420 / Math.max(fabricCanvas.getWidth(), fabricCanvas.getHeight()));
                const url = fabricCanvas.toDataURL({
                  format: 'jpeg',
                  quality: 0.82,
                  multiplier: mult,
                });
                if (!cancelled && myRun === runIdRef.current) {
                  setDataUrl(url);
                  setStatus('ready');
                }
              } catch {
                if (!cancelled && myRun === runIdRef.current) setStatus('error');
              } finally {
                finish();
              }
            };

            const forceRasterPartial = () => {
              if (settled || cancelled || myRun !== runIdRef.current) return;
              if (rasterDone) return;
              rasterDone = true;
              try {
                fabricCanvas.renderAll();
                const mult = Math.min(0.45, 420 / Math.max(fabricCanvas.getWidth(), fabricCanvas.getHeight()));
                const url = fabricCanvas.toDataURL({
                  format: 'jpeg',
                  quality: 0.78,
                  multiplier: mult,
                });
                if (!cancelled && myRun === runIdRef.current) {
                  setDataUrl(url);
                  setStatus('ready');
                }
              } catch {
                if (!cancelled && myRun === runIdRef.current) setStatus('error');
              } finally {
                finish();
              }
            };

            failSafe = window.setTimeout(() => {
              if (settled) return;
              if (!structureReady) structureReady = true;
              imageLoadsPending = 0;
              if (!rasterDone) {
                rasterDone = true;
                try {
                  fabricCanvas.renderAll();
                  const mult = Math.min(0.45, 420 / Math.max(fabricCanvas.getWidth(), fabricCanvas.getHeight()));
                  const url = fabricCanvas.toDataURL({
                    format: 'jpeg',
                    quality: 0.75,
                    multiplier: mult,
                  });
                  if (!cancelled && myRun === runIdRef.current) {
                    setDataUrl(url);
                    setStatus('ready');
                  }
                } catch {
                  if (!cancelled && myRun === runIdRef.current) setStatus('error');
                } finally {
                  finish();
                }
              }
            }, 22000);

            stallSafe = window.setTimeout(() => {
              if (settled || rasterDone) return;
              forceRasterPartial();
            }, 12000);

            try {
              fabricCanvas.loadFromJSON(
                json,
                () => {
                  if (cancelled || myRun !== runIdRef.current) {
                    dispose();
                    finish();
                    return;
                  }
                  structureReady = true;
                  tryRasterize();
                },
                (jsonObj, fabricObj) => {
                  if (!jsonObj || !fabricObj) return;
                  try {
                    if (jsonObj.fieldKey) fabricObj.set('fieldKey', jsonObj.fieldKey);
                    if (jsonObj.fieldConfig) fabricObj.set('fieldConfig', jsonObj.fieldConfig);
                  } catch {
                    /* ignore */
                  }
                  if (jsonObj.type !== 'image' || !jsonObj.src) return;
                  if (typeof fabricObj.setSrc !== 'function') return;

                  imageLoadsPending += 1;
                  const opts = imageSrcOpts(jsonObj.src);
                  const done = () => {
                    if (cancelled || myRun !== runIdRef.current) {
                      imageLoadsPending -= 1;
                      tryRasterize();
                      return;
                    }
                    try {
                      if (fabricObj && typeof fabricObj.setCoords === 'function') fabricObj.setCoords();
                    } catch {
                      /* canvas/object may be disposed */
                    }
                    imageLoadsPending -= 1;
                    tryRasterize();
                  };
                  try {
                    fabricObj.setSrc(jsonObj.src, done, opts);
                  } catch {
                    done();
                  }
                },
              );
            } catch {
              if (stallSafe) window.clearTimeout(stallSafe);
              if (failSafe) window.clearTimeout(failSafe);
              dispose();
              if (!cancelled && myRun === runIdRef.current) setStatus('error');
              finish();
            }
          });

          dispose();
        } catch {
          if (!cancelled && myRun === runIdRef.current) setStatus('error');
        }
      })();
    };

    scheduleId = window.setTimeout(start, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(scheduleId);
      // Avoid dispose() while loadFromJSON/enliven is still using the context (causes clearRect on null).
    };
  }, [status, json, rawW, rawH, listMode, tplKey]);

  if (hasSavedPreview) {
    return (
      <img
        src={savedPreview}
        alt=""
        className="h-full w-full object-cover"
      />
    );
  }

  if (!json) {
    return (
      <div className="flex h-full w-full items-center justify-center text-slate-300">
        <LayoutTemplate size={listMode ? 20 : 28} strokeWidth={1.5} className={listMode ? 'opacity-60' : ''} />
      </div>
    );
  }

  if (status === 'konva' && isKonvaTemplateJson(json)) {
    const objects = json.scene?.objects || [];
    const previewText = objects.find((o) => o.type === 'text' && o.text)?.text;
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gradient-to-b from-slate-50 to-slate-100 px-2 py-3 text-center">
        <LayoutTemplate size={listMode ? 18 : 24} strokeWidth={1.5} className="text-indigo-400" />
        <span className="text-[10px] font-medium text-slate-500">{objects.length} layer(s)</span>
        {previewText ? (
          <span className="line-clamp-3 text-[9px] leading-tight text-slate-600">{previewText}</span>
        ) : null}
        <span className="text-[8px] text-slate-400">Save to refresh preview</span>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative flex h-full w-full items-center justify-center overflow-hidden bg-slate-50">
      <canvas
        ref={canvasRef}
        key={tplKey}
        className="pointer-events-none fixed left-[-10000px] top-0 opacity-0"
        width={rawW}
        height={rawH}
        aria-hidden
      />
      {dataUrl ? (
        <img src={dataUrl} alt="" className="h-full w-full object-cover" />
      ) : status === 'error' ? (
        <LayoutTemplate size={listMode ? 20 : 28} strokeWidth={1.5} className={`text-slate-300 ${listMode ? 'opacity-60' : ''}`} />
      ) : (
        <LayoutTemplate size={listMode ? 20 : 28} strokeWidth={1.5} className="animate-pulse text-slate-300 opacity-70" />
      )}
    </div>
  );
}
